import './AppliedFiltersBar.scss';

import React, { useCallback, useMemo, useState } from 'react';

import { FilterOperator } from '../../../apis/graphql';
import ClearAllButton from '../../../common/Button/ClearAllButton';
import CollapsibleContent from '../../../common/CollapsibleContent';
import type { DropdownOption } from '../../../common/Dropdown';
import type { SearchParamsValuesType } from '../../../constants';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../constants';
import type { GlobalFilters } from '../../../contexts/FilterContext';
import { useFiltersContext } from '../../../contexts/FilterContext';
import bem from '../../../utils/bem';
import type { AdditionalFilterInDropdown } from '../TableFilterBar/AdditionalFilters/constants';
import {
  useAdditionalFilterDefinitions,
  useHandleDropdownFilters,
  useSelectedFiltersValues,
  useSyncSelectedDropdownFilters,
} from '../TableFilterBar/hooks';
import { CLEAR_ALL_BUTTON_LABEL } from './chipUtils';
import type { TopBarFilter } from './FilterValueChips';
import FilterValueChips from './FilterValueChips';

const [block] = bem('applied-filters-bar');

const COLLAPSED_MAX_ROWS = 2;
const CHIP_ROW_HEIGHT = 50;
const CHIP_ROW_GAP = 24;

export type AppliedFiltersBarProps = {
  applyCommonFilter: <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]) => void;
  onChipClick?: (filterId: string) => void;
};

const AppliedFiltersBar: React.FC<AppliedFiltersBarProps> = ({ applyCommonFilter, onChipClick }) => {
  const { applyCustomFilter, filters } = useFiltersContext();
  const selectedFiltersValues = useSelectedFiltersValues();

  const [selectedDropdownFilters, setSelectedDropdownFilters] = useState<AdditionalFilterInDropdown[]>([]);

  const filterDefinitions = useAdditionalFilterDefinitions(selectedFiltersValues);
  useSyncSelectedDropdownFilters({
    filters: filterDefinitions,
    selectedFiltersValues,
    onSelectedDropdownFilters: setSelectedDropdownFilters,
  });

  // KATANA-1509: the chip bar (useSelectedFiltersValues) surfaces Brand under the
  // legacy brandIdList/excludeBrandIdList search params (derived from brandIdOperator)
  // so it can reuse the existing chip-rendering pipeline below. Chip removal
  // (handleRemoveSingleValue / onRemoveSingleFilter / onRemoveAllFilter) writes back
  // to those legacy params, so redirect them into brandIdOperator here — the field
  // that's actually read for querying/saving — instead of writing to the legacy
  // fields directly, which nothing downstream of the chip bar reads anymore.
  const applyProductTypeFilter = useCallback(
    <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]) => {
      if (filterName === TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE) {
        applyCommonFilter('adProductList', filterValue as GlobalFilters['adProductList']);
        applyCommonFilter(
          TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE,
          null as GlobalFilters[typeof TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE]
        );
      } else if (
        filterName === TRAFFICKING_SEARCH_PARAMS.BRAND_ID_LIST ||
        filterName === TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST
      ) {
        const values = filterValue as GlobalFilters['brandIdList'];
        const operator =
          filterName === TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST
            ? FilterOperator.DoesNotContain
            : FilterOperator.Contains;
        applyCommonFilter('brandIdOperator', values && values.length > 0 ? { operator, values } : null);
      } else {
        applyCommonFilter(filterName, filterValue);
      }
    },
    [applyCommonFilter]
  );

  const {
    isFilterChipShown,
    onRemoveAllFilter,
    onRemoveSingleFilter,
    transformedSelectedDropdownFilters,
  } = useHandleDropdownFilters({
    selectedFiltersValues,
    applyCommonFilter: applyProductTypeFilter,
    selectedDropdownFilters,
    onSelectedDropdownFilters: setSelectedDropdownFilters,
  });

  const topBarFilters: TopBarFilter[] = useMemo(() => {
    const result: TopBarFilter[] = [];
    const accountManagerValues = filters.traffickerEmailList ?? [];
    if (accountManagerValues.length > 0) {
      result.push({ label: 'Account Manager', searchParam: 'traffickerEmailList', values: accountManagerValues });
    }
    const accountExecValues = filters.accountExecutiveList ?? [];
    if (accountExecValues.length > 0) {
      result.push({ label: 'Account Executive', searchParam: 'accountExecutiveList', values: accountExecValues });
    }
    return result;
  }, [filters.traffickerEmailList, filters.accountExecutiveList]);

  const [anySectionOverflows, setAnySectionOverflows] = useState(false);

  const hasAnyFilters = isFilterChipShown || topBarFilters.length > 0;

  const collapsedHeight = COLLAPSED_MAX_ROWS * CHIP_ROW_HEIGHT + (COLLAPSED_MAX_ROWS - 1) * CHIP_ROW_GAP;

  const handleClearAll = useCallback(() => {
    onRemoveAllFilter();
    applyCommonFilter('traffickerEmailList', ([] as unknown) as GlobalFilters['traffickerEmailList']);
    applyCommonFilter('accountExecutiveList', ([] as unknown) as GlobalFilters['accountExecutiveList']);
    applyCustomFilter(null);
  }, [onRemoveAllFilter, applyCommonFilter, applyCustomFilter]);

  const handleRemoveSingleValue = useCallback(
    (searchParam: SearchParamsValuesType, value: string) => {
      const currentValues = selectedFiltersValues[searchParam];

      if (Array.isArray(currentValues)) {
        const updatedValues = currentValues.filter((v) => {
          if (typeof v === 'string') return v !== value;
          return v.value !== value;
        });

        if (updatedValues.length === 0) {
          onRemoveSingleFilter(searchParam);
        } else {
          applyProductTypeFilter(searchParam, updatedValues as GlobalFilters[typeof searchParam]);
        }
      }
    },
    [selectedFiltersValues, applyProductTypeFilter, onRemoveSingleFilter]
  );

  const handleRemoveTopBarValue = useCallback(
    (searchParam: string, value: string) => {
      const filterKey = searchParam as keyof GlobalFilters;
      const currentValues = filters[filterKey] as DropdownOption[] | null;
      if (!currentValues) return;

      const updatedValues = currentValues.filter((v) => v.value !== value);
      applyCommonFilter(filterKey, updatedValues as GlobalFilters[typeof filterKey]);
    },
    [filters, applyCommonFilter]
  );

  if (!hasAnyFilters) return null;

  return (
    <div data-testid="filterChips">
      <CollapsibleContent
        collapsedMaxHeight={collapsedHeight}
        dependencies={[transformedSelectedDropdownFilters, topBarFilters]}
        className={block()}
        testId="applied-filters-bar"
        forceOverflowing={anySectionOverflows}
        footer={(): React.ReactNode => <ClearAllButton label={CLEAR_ALL_BUTTON_LABEL} onClick={handleClearAll} />}
      >
        {({ isExpanded }): React.ReactNode => (
          <FilterValueChips
            selectedFilters={transformedSelectedDropdownFilters}
            onRemoveValue={handleRemoveSingleValue}
            onRemoveFilter={onRemoveSingleFilter}
            topBarFilters={topBarFilters}
            onRemoveTopBarValue={handleRemoveTopBarValue}
            onChipClick={onChipClick}
            barExpanded={isExpanded}
            onAnySectionOverflow={setAnySectionOverflows}
          />
        )}
      </CollapsibleContent>
    </div>
  );
};

export default AppliedFiltersBar;
